import { useState, useEffect, useMemo } from "react";
import { api } from "@/App";
import { useI18n } from "@/i18n/I18nProvider";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  ReferenceArea,
  Scatter,
  ComposedChart,
} from "recharts";
import {
  Plus,
  Thermometer,
  AlertTriangle,
  CheckCircle,
  Loader2,
  TrendingDown,
  TrendingUp,
  Sun,
  Sunset,
} from "lucide-react";
import { toast } from "sonner";
import { format, parseISO, subDays, startOfDay } from "date-fns";
import { es as esLocale, fr as frLocale } from "date-fns/locale";
import NumericInput from "@/components/NumericInput";
import TempPresetButtons from "@/components/TempPresetButtons";

// ─────────────────────────────────────────────
// Constants / helpers
// ─────────────────────────────────────────────
const PALETTE = {
  normal: "#1B4D3E",     // deep green
  normalSoft: "rgba(27, 77, 62, 0.15)",
  danger: "#D9381E",     // warm red
  dangerSoft: "rgba(217, 56, 30, 0.10)",
  band: "rgba(27, 77, 62, 0.06)",
  refB: "#3B82F6",
  refC: "#8B5CF6",
  refD: "#F59E0B",
};

/** Map raw time_of_day (morning/afternoon/evening) to the user-facing shift concept
 *  Per product spec: Mañana = morning, Tarde = afternoon OR evening */
function shiftGroup(timeOfDay) {
  return timeOfDay === "morning" ? "morning" : "afternoon";
}

function shiftLabel(timeOfDay, t) {
  return shiftGroup(timeOfDay) === "morning" ? t("temp.morning") : t("temp.afternoon");
}

// ─────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────
export default function TemperatureLogs() {
  const { t, lang } = useI18n();
  const dateLocale = lang === "fr" ? frLocale : esLocale;

  // Server data
  const [refrigerators, setRefrigerators] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  // Filters (client-side)
  const [selectedFridge, setSelectedFridge] = useState("all");
  const [period, setPeriod] = useState("7d"); // today | 7d | 30d | all
  const [shift, setShift] = useState("all");   // all | morning | afternoon
  const [onlyOutOfRange, setOnlyOutOfRange] = useState(false);

  // UI
  const [showAddModal, setShowAddModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [listLimit, setListLimit] = useState(15);

  const [newLog, setNewLog] = useState({
    refrigerator_id: "",
    temperature: "",
    time_of_day: "morning",
    notes: "",
  });

  // ── Fetch ───────────────────────────────────
  useEffect(() => {
    let cancelled = false;
    async function fetchAll() {
      setLoading(true);
      setLoadError(false);
      try {
        const [fRes, lRes] = await Promise.all([
          api.get("/refrigerators"),
          api.get("/temperature-logs"),
        ]);
        if (cancelled) return;
        setRefrigerators(fRes.data || []);
        setLogs(lRes.data || []);
      } catch (err) {
        if (cancelled) return;
        console.error("[TemperatureLogs] load failed:", err);
        setLoadError(true);
        toast.error(t("temp.loadError"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    fetchAll();
    return () => { cancelled = true; };
  }, [t]);

  // ── Derived: filtered logs ─────────────────
  const filteredLogs = useMemo(() => {
    if (!logs.length) return [];
    const now = new Date();
    let periodStart = null;
    if (period === "today") periodStart = startOfDay(now);
    else if (period === "7d") periodStart = subDays(now, 7);
    else if (period === "30d") periodStart = subDays(now, 30);

    return logs.filter((l) => {
      if (selectedFridge !== "all" && l.refrigerator_id !== selectedFridge) return false;
      if (shift !== "all" && shiftGroup(l.time_of_day) !== shift) return false;
      if (onlyOutOfRange && l.is_within_range) return false;
      if (periodStart) {
        const t = l.logged_at ? new Date(l.logged_at) : null;
        if (!t || t < periodStart) return false;
      }
      return true;
    });
  }, [logs, selectedFridge, period, shift, onlyOutOfRange]);

  // ── Derived: summary stats ─────────────────
  const stats = useMemo(() => {
    if (!filteredLogs.length) {
      return { count: 0, avg: null, min: null, max: null, outOfRange: 0 };
    }
    let sum = 0, min = Infinity, max = -Infinity, ooR = 0;
    for (const l of filteredLogs) {
      const v = Number(l.temperature);
      if (Number.isFinite(v)) {
        sum += v;
        if (v < min) min = v;
        if (v > max) max = v;
      }
      if (!l.is_within_range) ooR += 1;
    }
    return {
      count: filteredLogs.length,
      avg: (sum / filteredLogs.length).toFixed(1),
      min: Number.isFinite(min) ? min.toFixed(1) : null,
      max: Number.isFinite(max) ? max.toFixed(1) : null,
      outOfRange: ooR,
    };
  }, [filteredLogs]);

  // ── Derived: chart data ────────────────────
  // Chronologically ascending (recharts renders left→right)
  const chartData = useMemo(() => {
    return [...filteredLogs]
      .slice()
      .sort((a, b) => new Date(a.logged_at) - new Date(b.logged_at))
      .map((l) => ({
        log_id: l.log_id,
        ts: l.logged_at,
        temperature: Number(l.temperature),
        in_range: l.is_within_range,
        refrigerator_id: l.refrigerator_id,
        refrigerator_name: refrigerators.find((f) => f.refrigerator_id === l.refrigerator_id)?.name || "—",
        time_of_day: l.time_of_day,
        shift: shiftGroup(l.time_of_day),
      }));
  }, [filteredLogs, refrigerators]);

  const selectedFridgeData = refrigerators.find((f) => f.refrigerator_id === selectedFridge);

  // For "all refrigerators" view, split data per fridge (so lines don't cross)
  const seriesByFridge = useMemo(() => {
    if (selectedFridge !== "all") return null;
    const map = new Map();
    for (const d of chartData) {
      const key = d.refrigerator_id;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(d);
    }
    // Assign colors deterministically
    const colors = [PALETTE.normal, PALETTE.refB, PALETTE.refC, PALETTE.refD];
    return Array.from(map.entries()).map(([id, data], idx) => ({
      id,
      name: refrigerators.find((f) => f.refrigerator_id === id)?.name || "—",
      color: colors[idx % colors.length],
      data,
    }));
  }, [chartData, refrigerators, selectedFridge]);

  // ── Actions ────────────────────────────────
  const handleAddLog = async (e) => {
    e.preventDefault();
    if (!newLog.refrigerator_id || newLog.temperature === "") {
      toast.error("Complete refrigerador y temperatura");
      return;
    }
    setSubmitting(true);
    try {
      const res = await api.post("/temperature-logs", {
        ...newLog,
        temperature: parseFloat(newLog.temperature),
      });
      setLogs((prev) => [res.data, ...prev]);
      setShowAddModal(false);
      setNewLog({ refrigerator_id: "", temperature: "", time_of_day: "morning", notes: "" });
      if (res.data.is_within_range) {
        toast.success("Registro guardado correctamente");
      } else {
        toast.warning("Registro guardado — Fuera de rango");
      }
    } catch (err) {
      console.error("[TemperatureLogs] create failed:", err);
      toast.error(err.response?.data?.detail || "Error al guardar");
    } finally {
      setSubmitting(false);
    }
  };

  const formatDateTime = (iso) => {
    if (!iso) return "—";
    try {
      // Ex: 11 jul. 2026 · 18:29
      return format(parseISO(iso), "d MMM yyyy · HH:mm", { locale: dateLocale });
    } catch {
      return iso;
    }
  };

  // ── Render ─────────────────────────────────
  return (
    <div data-testid="temperature-logs-page" className="space-y-4 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-heading font-bold text-2xl text-foreground tracking-tight">
            {t("temp.title")}
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">{t("temp.subtitle")}</p>
        </div>
        <Button onClick={() => setShowAddModal(true)} size="sm" data-testid="add-temp-log-btn">
          <Plus className="h-3.5 w-3.5 mr-1.5" />
          {t("temp.logTemp")}
        </Button>
      </div>

      {/* Filter bar */}
      <Card>
        <CardContent className="p-3">
          <div className="flex flex-col md:flex-row md:flex-wrap md:items-center gap-2 md:gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <Label className="text-xs whitespace-nowrap text-muted-foreground">
                {t("temp.refrigerator")}
              </Label>
              <Select value={selectedFridge} onValueChange={setSelectedFridge}>
                <SelectTrigger className="h-8 text-xs min-w-[180px]" data-testid="fridge-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("temp.allRefrigerators")}</SelectItem>
                  {refrigerators.map((f) => (
                    <SelectItem key={f.refrigerator_id} value={f.refrigerator_id}>
                      {f.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <Label className="text-xs whitespace-nowrap text-muted-foreground">
                {t("temp.period")}
              </Label>
              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger className="h-8 text-xs w-[110px]" data-testid="period-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="today">{t("temp.periodToday")}</SelectItem>
                  <SelectItem value="7d">{t("temp.period7d")}</SelectItem>
                  <SelectItem value="30d">{t("temp.period30d")}</SelectItem>
                  <SelectItem value="all">{t("temp.periodAll")}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2">
              <Label className="text-xs whitespace-nowrap text-muted-foreground">
                {t("temp.shift")}
              </Label>
              <Select value={shift} onValueChange={setShift}>
                <SelectTrigger className="h-8 text-xs w-[130px]" data-testid="shift-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("temp.shiftAll")}</SelectItem>
                  <SelectItem value="morning">{t("temp.morning")}</SelectItem>
                  <SelectItem value="afternoon">{t("temp.afternoon")}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center gap-2 md:ml-auto">
              <Switch
                id="only-oor"
                checked={onlyOutOfRange}
                onCheckedChange={setOnlyOutOfRange}
                data-testid="only-oor-switch"
              />
              <Label htmlFor="only-oor" className="text-xs cursor-pointer">
                {t("temp.onlyOOR")}
              </Label>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Loading / error */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : loadError ? (
        <Card>
          <CardContent className="p-6 flex flex-col items-center gap-2 text-destructive">
            <AlertTriangle className="h-6 w-6" />
            <p className="text-sm">{t("temp.loadError")}</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <StatCard
              label={t("temp.avgTemp")}
              value={stats.avg !== null ? `${stats.avg}°C` : "—"}
              icon={Thermometer}
              tone="primary"
              testid="stat-avg"
            />
            <StatCard
              label={t("temp.minTemp")}
              value={stats.min !== null ? `${stats.min}°C` : "—"}
              icon={TrendingDown}
              tone="muted"
              testid="stat-min"
            />
            <StatCard
              label={t("temp.maxTemp")}
              value={stats.max !== null ? `${stats.max}°C` : "—"}
              icon={TrendingUp}
              tone="muted"
              testid="stat-max"
            />
            <StatCard
              label={t("temp.outOfRange")}
              value={stats.outOfRange}
              icon={stats.outOfRange > 0 ? AlertTriangle : CheckCircle}
              tone={stats.outOfRange > 0 ? "danger" : "primary"}
              testid="stat-oor"
            />
          </div>

          {/* Chart */}
          <Card>
            <CardHeader className="p-4 pb-2">
              <CardTitle className="font-heading text-base flex items-center gap-2">
                <Thermometer className="h-4 w-4 text-primary" />
                {t("temp.chartTitle")}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 pt-1">
              {chartData.length === 0 ? (
                <div className="h-56 flex flex-col items-center justify-center text-muted-foreground text-sm gap-2">
                  <Thermometer className="h-8 w-8 opacity-40" />
                  {stats.count === 0 && logs.length > 0
                    ? t("temp.noResults")
                    : t("temp.chartEmpty")}
                </div>
              ) : (
                <TemperatureChart
                  chartData={chartData}
                  seriesByFridge={seriesByFridge}
                  selectedFridgeData={selectedFridgeData}
                  formatDateTime={formatDateTime}
                  t={t}
                />
              )}
            </CardContent>
          </Card>

          {/* Recent logs list */}
          <Card>
            <CardHeader className="p-4 pb-2">
              <CardTitle className="font-heading text-base">
                {t("temp.recentLogs")}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 pt-2">
              {logs.length === 0 ? (
                <EmptyState icon={Thermometer} label={t("temp.noLogs")} />
              ) : filteredLogs.length === 0 ? (
                <EmptyState icon={Thermometer} label={t("temp.noResults")} />
              ) : (
                <>
                  <ul className="space-y-1.5" data-testid="temp-log-list">
                    {filteredLogs.slice(0, listLimit).map((log) => (
                      <LogRow
                        key={log.log_id}
                        log={log}
                        refrigerators={refrigerators}
                        formatDateTime={formatDateTime}
                        t={t}
                      />
                    ))}
                  </ul>
                  {filteredLogs.length > listLimit && (
                    <div className="mt-3 flex justify-center">
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-xs h-8"
                        onClick={() => setListLimit((n) => n + 15)}
                        data-testid="view-more-btn"
                      >
                        {t("temp.viewMore")} ({filteredLogs.length - listLimit})
                      </Button>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* Add-log modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent data-testid="add-temp-log-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{t("temp.logTemp")}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleAddLog} className="space-y-4">
            <div className="space-y-2">
              <Label>{t("temp.refrigerator")} *</Label>
              <Select
                value={newLog.refrigerator_id}
                onValueChange={(v) => setNewLog((prev) => ({ ...prev, refrigerator_id: v }))}
              >
                <SelectTrigger data-testid="modal-fridge-select">
                  <SelectValue placeholder={t("temp.selectRefrigerator")} />
                </SelectTrigger>
                <SelectContent>
                  {refrigerators.map((f) => (
                    <SelectItem key={f.refrigerator_id} value={f.refrigerator_id}>
                      {f.name} ({f.min_temp}°C — {f.max_temp}°C)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t("temp.temperature")} *</Label>
              <NumericInput
                placeholder="3.5  ó  -18"
                value={newLog.temperature}
                onChange={(e) => setNewLog((prev) => ({ ...prev, temperature: e.target.value }))}
                data-testid="temperature-input"
              />
              <TempPresetButtons
                onSelect={(v) => setNewLog((prev) => ({ ...prev, temperature: v }))}
                data-testid="temp-log-presets"
              />
            </div>
            <div className="space-y-2">
              <Label>{t("temp.selectTime")}</Label>
              <Select
                value={newLog.time_of_day}
                onValueChange={(v) => setNewLog((prev) => ({ ...prev, time_of_day: v }))}
              >
                <SelectTrigger data-testid="time-of-day-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="morning">{t("temp.morning")}</SelectItem>
                  <SelectItem value="afternoon">{t("temp.afternoon")}</SelectItem>
                  <SelectItem value="evening">{t("temp.evening")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>{t("temp.notes")}</Label>
              <Textarea
                value={newLog.notes}
                onChange={(e) => setNewLog((prev) => ({ ...prev, notes: e.target.value }))}
                data-testid="notes-textarea"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAddModal(false)}>
                {t("common.cancel")}
              </Button>
              <Button type="submit" disabled={submitting} data-testid="submit-temp-log-btn">
                {submitting ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Plus className="h-4 w-4 mr-2" />
                )}
                {submitting ? t("temp.logging") : t("temp.logTemp")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─────────────────────────────────────────────
// Sub-components
// ─────────────────────────────────────────────
function StatCard({ label, value, icon: Icon, tone, testid }) {
  const toneCfg = {
    primary: { bg: "bg-primary/10", text: "text-primary" },
    danger:  { bg: "bg-red-50",     text: "text-destructive" },
    muted:   { bg: "bg-zinc-100",   text: "text-zinc-600" },
  }[tone] || { bg: "bg-zinc-100", text: "text-zinc-600" };

  return (
    <Card data-testid={testid}>
      <CardContent className="p-3 sm:p-4">
        <div className="flex items-center justify-between">
          <div className="min-w-0">
            <p className="text-[10px] sm:text-xs text-muted-foreground uppercase tracking-wide font-medium truncate">
              {label}
            </p>
            <p className="text-lg sm:text-2xl font-bold font-mono mt-0.5 truncate">{value}</p>
          </div>
          <div className={`w-8 h-8 sm:w-9 sm:h-9 rounded-md ${toneCfg.bg} flex items-center justify-center flex-shrink-0`}>
            <Icon className={`h-4 w-4 ${toneCfg.text}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function EmptyState({ icon: Icon, label }) {
  return (
    <div className="flex flex-col items-center justify-center h-32 text-muted-foreground gap-2">
      <Icon className="h-8 w-8 opacity-40" />
      <p className="text-sm">{label}</p>
    </div>
  );
}

function LogRow({ log, refrigerators, formatDateTime, t }) {
  const fridge = refrigerators.find((f) => f.refrigerator_id === log.refrigerator_id);
  const inRange = log.is_within_range;
  const isMorning = shiftGroup(log.time_of_day) === "morning";
  const ShiftIcon = isMorning ? Sun : Sunset;

  return (
    <li
      className={`flex items-center justify-between gap-3 p-2.5 rounded-md border transition-colors ${
        inRange
          ? "border-border bg-zinc-50/40 hover:bg-zinc-50"
          : "border-red-200 bg-red-50/60 hover:bg-red-50"
      }`}
      data-testid={`temp-log-row-${log.log_id}`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <div
          className={`w-9 h-9 rounded-md flex items-center justify-center flex-shrink-0 ${
            inRange ? "bg-primary/10" : "bg-red-100"
          }`}
        >
          <Thermometer
            className={`h-4 w-4 ${inRange ? "text-primary" : "text-destructive"}`}
            aria-hidden="true"
          />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium truncate">{fridge?.name || "—"}</p>
          <p className="text-[11px] text-muted-foreground flex items-center gap-1.5 flex-wrap">
            <span>{formatDateTime(log.logged_at)}</span>
            <span aria-hidden="true">·</span>
            <span className="inline-flex items-center gap-1">
              <ShiftIcon className="h-3 w-3" aria-hidden="true" />
              {shiftLabel(log.time_of_day, t)}
            </span>
          </p>
        </div>
      </div>
      <div className="flex flex-col items-end gap-0.5 flex-shrink-0">
        <p
          className={`text-lg font-bold font-mono ${
            inRange ? "text-foreground" : "text-destructive"
          }`}
        >
          {log.temperature}°C
        </p>
        <Badge
          variant="outline"
          className={`h-4 px-1.5 text-[9px] font-medium border ${
            inRange
              ? "text-primary border-primary/30 bg-primary/5"
              : "text-destructive border-destructive/40 bg-red-50"
          }`}
        >
          {inRange ? t("temp.inRangeLabel") : t("temp.outOfRangeLabel")}
        </Badge>
      </div>
    </li>
  );
}

// ─── Chart ─────────────────────────────────
function TemperatureChart({ chartData, seriesByFridge, selectedFridgeData, formatDateTime, t }) {
  // For all-refrigerators: overlay multiple Line series (each with only its own points).
  // For single fridge: single line + reference band + reference lines + colored dots.
  const isAll = !!seriesByFridge;

  const xTickFormatter = (ts) => {
    try {
      return format(parseISO(ts), "d MMM · HH:mm");
    } catch {
      return "";
    }
  };

  return (
    <div className="w-full h-56 sm:h-72" data-testid="temperature-chart">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart
          data={isAll ? undefined : chartData}
          margin={{ top: 8, right: 12, left: -12, bottom: 4 }}
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#E4E4E7" />
          <XAxis
            dataKey="ts"
            type="category"
            allowDuplicatedCategory={false}
            fontSize={10}
            tick={{ fill: "#71717A" }}
            tickFormatter={xTickFormatter}
            minTickGap={30}
          />
          <YAxis
            fontSize={10}
            tick={{ fill: "#71717A" }}
            domain={["auto", "auto"]}
            unit="°"
          />
          <Tooltip
            content={<ChartTooltip formatDateTime={formatDateTime} t={t} />}
          />

          {selectedFridgeData && (
            <>
              <ReferenceArea
                y1={selectedFridgeData.min_temp}
                y2={selectedFridgeData.max_temp}
                fill={PALETTE.band}
                strokeOpacity={0}
                ifOverflow="extendDomain"
              />
              <ReferenceLine
                y={selectedFridgeData.min_temp}
                stroke={PALETTE.normal}
                strokeDasharray="4 4"
                strokeOpacity={0.6}
                label={{ value: `min ${selectedFridgeData.min_temp}°`, fill: PALETTE.normal, fontSize: 9, position: "insideTopLeft" }}
              />
              <ReferenceLine
                y={selectedFridgeData.max_temp}
                stroke={PALETTE.danger}
                strokeDasharray="4 4"
                strokeOpacity={0.6}
                label={{ value: `max ${selectedFridgeData.max_temp}°`, fill: PALETTE.danger, fontSize: 9, position: "insideBottomLeft" }}
              />
            </>
          )}

          {isAll ? (
            // Multiple series, no artificial cross-fridge line joins
            seriesByFridge.map((s) => (
              <Line
                key={s.id}
                dataKey="temperature"
                data={s.data}
                name={s.name}
                type="monotone"
                stroke={s.color}
                strokeWidth={1.8}
                dot={(props) => renderDot(props, s.color)}
                activeDot={{ r: 5 }}
                connectNulls={false}
                isAnimationActive={false}
              />
            ))
          ) : (
            <Line
              dataKey="temperature"
              type="monotone"
              stroke={PALETTE.normal}
              strokeWidth={1.8}
              dot={(props) => renderDot(props, PALETTE.normal)}
              activeDot={{ r: 5 }}
              isAnimationActive={false}
            />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

/** Render dot in red when the point is out of range, otherwise use the series color. */
function renderDot(props, seriesColor) {
  const { cx, cy, payload, key } = props;
  if (cx == null || cy == null || !payload) return null;
  const color = payload.in_range ? seriesColor : PALETTE.danger;
  return (
    <circle
      key={key || `dot-${payload.log_id}`}
      cx={cx}
      cy={cy}
      r={3.2}
      fill={color}
      stroke="#fff"
      strokeWidth={1}
    />
  );
}

function ChartTooltip({ active, payload, formatDateTime, t }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0]?.payload;
  if (!p) return null;
  return (
    <div className="rounded-md border bg-white shadow-md p-2.5 text-xs space-y-0.5 min-w-[180px]">
      <p className="font-semibold text-foreground">{p.refrigerator_name}</p>
      <p className="text-muted-foreground">{formatDateTime(p.ts)}</p>
      <div className="flex items-center justify-between pt-1">
        <span className="text-muted-foreground">Temp:</span>
        <span className={`font-mono font-bold ${p.in_range ? "text-primary" : "text-destructive"}`}>
          {p.temperature}°C
        </span>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-muted-foreground">{t("temp.shift")}:</span>
        <span>{p.shift === "morning" ? t("temp.morning") : t("temp.afternoon")}</span>
      </div>
      <div className="flex items-center justify-between">
        <span className="text-muted-foreground">{t("temp.status")}:</span>
        <span className={`font-medium ${p.in_range ? "text-primary" : "text-destructive"}`}>
          {p.in_range ? t("temp.inRangeLabel") : t("temp.outOfRangeLabel")}
        </span>
      </div>
    </div>
  );
}
